import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Use the express proxy API
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_EXPRESS_API_URL,
  credentials: 'include', // Include cookies for authentication
  prepareHeaders: async (headers) => {
    headers.set("Content-Type", "application/json");

    // Add authentication if needed
    const token = localStorage.getItem("bugtracker_accessToken");
    if (token) {
      headers.set("accessToken", token);
    }

    return headers;
  },
});

export interface IpGeoLocation {
  status: string;
  country?: string;
  regionName?: string;
  city?: string;
  lat?: number;
  lon?: number;
  isp?: string;
  ipAddress?: string;
}

interface ProxyResponse {
  response?: {
    data?: IpGeoLocation;
  };
  // Direct response format (if proxy returns data directly)
  status?: string;
  country?: string;
  regionName?: string;
  city?: string;
  lat?: number;
  lon?: number;
  isp?: string;
}

export const ipGeoLocationApi = createApi({
  reducerPath: "ipGeoLocationApi",
  baseQuery,
  endpoints: (builder) => ({
    getIpGeolocation: builder.query<IpGeoLocation, string>({
      query: (ip) => ({
        url: "/request/execute",
        method: "POST",
        body: {
          requestData: {
            reqMethod: "GET",
            url: `http://ip-api.com/json/${ip}?fields=status,country,regionName,city,lat,lon,isp`,
            reqHeaders: {},
          },
        },
      }),
      transformResponse: (response: ProxyResponse, _meta, ip) => {
        // Handle both wrapped and direct response formats
        const data = response?.response?.data || response;
        console.log('IP Geolocation response for', ip, ':', data);
        return {
          ...data,
          ipAddress: ip,
        } as IpGeoLocation;
      },
    }),
    getMultipleIpGeolocations: builder.query<IpGeoLocation[], string[]>({
      async queryFn(ips, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const results = await Promise.all(
            ips.map(async (ip) => {
              const result = await fetchWithBQ({
                url: "/request/execute",
                method: "POST",
                body: {
                  requestData: {
                    reqMethod: "GET",
                    url: `http://ip-api.com/json/${ip}?fields=status,country,regionName,city,lat,lon,isp`,
                    reqHeaders: {},
                  },
                },
              });

              if (result.error) {
                console.error(`Failed to fetch geolocation for IP ${ip}:`, result.error);
                return null;
              }

              const proxyResponse = result.data as ProxyResponse;
              // Handle both wrapped and direct response formats
              const data = proxyResponse?.response?.data || proxyResponse;

              console.log('IP Geolocation response for', ip, ':', data);

              if (data && data.status === "success") {
                return { ...data, ipAddress: ip } as IpGeoLocation;
              }
              return null;
            })
          );

          return {
            data: results.filter((r) => r !== null) as IpGeoLocation[],
          };
        } catch (error) {
          console.error("Failed to fetch IP geolocations:", error);
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
    }),
  }),
});

export const { useGetIpGeolocationQuery, useGetMultipleIpGeolocationsQuery } =
  ipGeoLocationApi;
